/**
 * `MessageList` — scrollable list of message bubbles (ARCHITECTURE §7).
 * Auto-scrolls to the newest message. The wallpaper honors the org's chat
 * background (solid or gradient) via the `--lc-chat-bg` / `--lc-chat-bg-gradient`
 * vars (flat when no gradient is set). Passes the org theme to each bubble so
 * avatars + bubble text colors render.
 */

import { useEffect, useRef } from 'react';
import type { Message, OrgTheme } from '@flozic/shared/types';
import { MessageBubble } from './MessageBubble';

export interface MessageListProps {
  messages: Message[];
  theme?: OrgTheme;
  /** Greeting shown as the empty-state prompt (from `WidgetConfig.welcomeMessage`). */
  welcomeMessage?: string;
}

/** Wallpaper background: linear-gradient across the two vars (equal stops → flat). */
const WALLPAPER_STYLE: React.CSSProperties = {
  backgroundImage: 'linear-gradient(160deg, var(--lc-chat-bg), var(--lc-chat-bg-gradient))',
};

export function MessageList({ messages, theme, welcomeMessage }: MessageListProps) {
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Keep the newest message in view on each append.
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  if (messages.length === 0) {
    return (
      <div
        className="flex flex-1 items-center justify-center p-6 text-center text-sm text-lc-text/60"
        style={WALLPAPER_STYLE}
      >
        {welcomeMessage?.trim() || 'Say hello to start the conversation.'}
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-3" style={WALLPAPER_STYLE}>
      {messages.map((m) => (
        <MessageBubble key={m.id} message={m} theme={theme} />
      ))}
      <div ref={endRef} />
    </div>
  );
}
